<template>
  <div class="flex flex-col items-center gap-3">
    <div
      class="relative overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-move select-none touch-none"
      :class="shape === 'circle' ? 'rounded-full' : 'rounded-xl'"
      :style="{ width: `${viewportSize}px`, height: `${viewportSize}px` }"
      @pointerdown="startDrag"
      @pointermove="onDrag"
      @pointerup="endDrag"
      @pointercancel="endDrag"
      @wheel.prevent="onWheel"
    >
      <img
        v-show="loaded"
        ref="imageEl"
        :src="imageSrc"
        draggable="false"
        class="absolute top-0 left-0 max-w-none pointer-events-none"
        :style="imageStyle"
        @load="onImageLoad"
      />
    </div>

    <div class="flex items-center gap-2 w-full max-w-[220px]">
      <button type="button" title="Zoom out" aria-label="Zoom out" @click="zoomOutStep" :disabled="scale <= minScale"
        class="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
        </svg>
      </button>
      <input type="range" :min="minScale" :max="maxScale" step="0.001" :value="scale"
        @input="zoomTo(Number($event.target.value))"
        class="flex-1 accent-blue-600 cursor-pointer" />
      <button type="button" title="Zoom in" aria-label="Zoom in" @click="zoomInStep" :disabled="scale >= maxScale"
        class="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM10.5 7.5v6m-3-3h6"/>
        </svg>
      </button>
    </div>
    <p class="text-[11px] text-gray-400 dark:text-gray-500">Drag to reposition. Scroll, use the slider, or tap the zoom buttons.</p>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  imageSrc: { type: String, required: true },
  shape: { type: String, default: 'circle' },
  viewportSize: { type: Number, default: 260 },
  outputSize: { type: Number, default: 500 },
})

const imageEl = ref(null)
const loaded = ref(false)

const naturalWidth = ref(0)
const naturalHeight = ref(0)
const scale = ref(1)
const minScale = ref(0.1)
const maxScale = ref(3)
const imgLeft = ref(0)
const imgTop = ref(0)

const dragging = ref(false)
let dragStartX = 0
let dragStartY = 0
let dragStartLeft = 0
let dragStartTop = 0

const imageStyle = computed(() => ({
  width: `${naturalWidth.value * scale.value}px`,
  height: `${naturalHeight.value * scale.value}px`,
  transform: `translate(${imgLeft.value}px, ${imgTop.value}px)`,
}))

// Below "cover" scale the image is smaller than the frame in one or both
// dimensions (deliberate — lets a user zoom out past cover-fit to leave
// some padding/margin around the photo instead of a tight crop). In that
// case there's nothing to drag against, so center it instead of pinning it
// to a corner the way the drag-range math below would otherwise do.
function clamp() {
  const width = naturalWidth.value * scale.value
  const height = naturalHeight.value * scale.value

  if (width <= props.viewportSize) {
    imgLeft.value = (props.viewportSize - width) / 2
  } else {
    const minLeft = props.viewportSize - width
    imgLeft.value = Math.min(0, Math.max(minLeft, imgLeft.value))
  }

  if (height <= props.viewportSize) {
    imgTop.value = (props.viewportSize - height) / 2
  } else {
    const minTop = props.viewportSize - height
    imgTop.value = Math.min(0, Math.max(minTop, imgTop.value))
  }
}

function onImageLoad() {
  naturalWidth.value = imageEl.value.naturalWidth
  naturalHeight.value = imageEl.value.naturalHeight

  // "Cover" fit — the smaller dimension is scaled up to exactly fill the
  // viewport, so there's never an empty gap at the default zoom level.
  const coverScale = Math.max(props.viewportSize / naturalWidth.value, props.viewportSize / naturalHeight.value)
  // Allowed to zoom out further than cover-fit (down to half of it), so a
  // user can deliberately leave some padding/margin around the photo
  // instead of always being forced into a tight, edge-to-edge crop.
  minScale.value = coverScale * 0.5
  maxScale.value = coverScale * 4
  scale.value = coverScale

  imgLeft.value = (props.viewportSize - naturalWidth.value * scale.value) / 2
  imgTop.value = (props.viewportSize - naturalHeight.value * scale.value) / 2
  loaded.value = true
}

function startDrag(e) {
  if (!loaded.value) return
  dragging.value = true
  dragStartX = e.clientX
  dragStartY = e.clientY
  dragStartLeft = imgLeft.value
  dragStartTop = imgTop.value
  e.currentTarget.setPointerCapture(e.pointerId)
}

function onDrag(e) {
  if (!dragging.value) return
  imgLeft.value = dragStartLeft + (e.clientX - dragStartX)
  imgTop.value = dragStartTop + (e.clientY - dragStartY)
  clamp()
}

function endDrag() {
  dragging.value = false
}

// Zooms while keeping whatever's currently at the viewport's center fixed in
// place, rather than zooming from the top-left corner — matches how Facebook/
// Twitter-style avatar croppers feel when you drag the slider or scroll.
function zoomTo(nextScale) {
  const clamped = Math.min(maxScale.value, Math.max(minScale.value, nextScale))
  if (clamped === scale.value) return

  const center = props.viewportSize / 2
  const imagePointX = (center - imgLeft.value) / scale.value
  const imagePointY = (center - imgTop.value) / scale.value

  scale.value = clamped
  imgLeft.value = center - imagePointX * clamped
  imgTop.value = center - imagePointY * clamped
  clamp()
}

function onWheel(e) {
  zoomTo(scale.value + (e.deltaY > 0 ? -0.05 : 0.05) * scale.value)
}

// A single click should feel like a deliberate step, not the tiny nudge a
// wheel tick gives — 20% of the current scale per click.
function zoomInStep() {
  zoomTo(scale.value * 1.2)
}

function zoomOutStep() {
  zoomTo(scale.value * 0.8)
}

// Reads directly from the full-resolution source image (canvas drawImage's
// source rect is always in natural pixels, regardless of the element's
// on-screen CSS size), so the crop never loses quality relative to the original.
function getCroppedBlob(mimeType = 'image/jpeg', quality = 0.9) {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    canvas.width = props.outputSize
    canvas.height = props.outputSize
    const ctx = canvas.getContext('2d')

    // Matches the frame's own background — without this, any padding left by
    // zooming out past cover-fit would export as black (JPEG has no alpha
    // channel, so canvas fills transparent regions with black on encode).
    ctx.fillStyle = '#f3f4f6'
    ctx.fillRect(0, 0, props.outputSize, props.outputSize)

    const sourceX = -imgLeft.value / scale.value
    const sourceY = -imgTop.value / scale.value
    const sourceSize = props.viewportSize / scale.value

    ctx.drawImage(imageEl.value, sourceX, sourceY, sourceSize, sourceSize, 0, 0, props.outputSize, props.outputSize)
    canvas.toBlob((blob) => resolve(blob), mimeType, quality)
  })
}

defineExpose({ getCroppedBlob })
</script>
